import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

import { alpha } from "@mui/material/styles";
import { green, grey, orange, red } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";

const sectionCardSx = {
	border: `1px solid ${grey[200]}`,
	borderRadius: 3,
	p: 3,
	background: "white",
	boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const DEFAULT_RANGE_DAYS = 7;

const getDefaultRange = () => {
	const end = dayjs();
	const start = end.subtract(DEFAULT_RANGE_DAYS - 1, "day");
	return {
		start: start.format("YYYY-MM-DD"),
		end: end.format("YYYY-MM-DD"),
	};
};

const toSafeNumber = (value) => {
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

const buildTrendMeta = (currentValue, previousValue) => {
	const current = toSafeNumber(currentValue);
	const previous = toSafeNumber(previousValue);
	if (current === null || previous === null) return null;
	const delta = current - previous;
	const percentage = previous === 0 ? (delta === 0 ? 0 : 100) : Math.round((delta / previous) * 100);
	return { delta, percentage };
};

const getCostBreakdownTrend = (item, previousMap) => {
	const explicitPercentKeys = ["changePercentage", "trendPercentage", "growthPercentage", "momPercentage", "percentageChange", "deltaPercentage"];
	for (const key of explicitPercentKeys) {
		const value = toSafeNumber(item?.[key]);
		if (value !== null) return { delta: value, percentage: Math.round(value) };
	}

	const explicitGrowth = toSafeNumber(item?.growth);
	if (explicitGrowth !== null) return { delta: explicitGrowth, percentage: Math.round(explicitGrowth) };

	const explicitDegrowth = toSafeNumber(item?.degrowth);
	if (explicitDegrowth !== null) return { delta: -Math.abs(explicitDegrowth), percentage: -Math.round(Math.abs(explicitDegrowth)) };

	const prevFromItem = item?.previousValue ?? item?.prevValue;
	const trendFromItem = buildTrendMeta(item?.value, prevFromItem);
	if (trendFromItem) return trendFromItem;

	const previousByLabel = previousMap?.[item?.label];
	return buildTrendMeta(item?.value, previousByLabel?.value);
};

const ShootSummary = () => {
	const { setSnackBar, setSnackBarOpen } = GlobalState();

	const [loading, setLoading] = useState(false);
	const [analytics, setAnalytics] = useState({});
	const [merchantAnalytics, setMerchantAnalytics] = useState({});
	const [dateRange, setDateRange] = useState(getDefaultRange);
	const [draftRange, setDraftRange] = useState(getDefaultRange);
	const [selectedPreset, setSelectedPreset] = useState(7);
	const [trendGranularity, setTrendGranularity] = useState("month");
	const [debouncedDateRange, setDebouncedDateRange] = useState(getDefaultRange);
	const latestRequestRef = useRef(0);

	const setPresetRange = (days) => {
		const end = dayjs();
		const start = end.subtract(days - 1, "day");
		const updated = {
			start: start.format("YYYY-MM-DD"),
			end: end.format("YYYY-MM-DD"),
		};
		setDateRange(updated);
		setDraftRange(updated);
		setSelectedPreset(days);
	};

	const handleApplyDateRange = () => {
		const start = dayjs(draftRange.start);
		const end = dayjs(draftRange.end);

		if (!start.isValid() || !end.isValid()) {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({ ...prev, message: "Please enter a valid start and end date", severity: "error" }));
			return;
		}
		if (end.isBefore(start, "day")) {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({ ...prev, message: "End date cannot be before start date", severity: "error" }));
			return;
		}

		setDateRange({ ...draftRange });
		setSelectedPreset(null);
	};

	const fetchSummaryData = useCallback(
		async (range, granularity) => {
			if (!range?.start || !range?.end) return;
			const requestId = Date.now();
			latestRequestRef.current = requestId;
			setLoading(true);
			try {
				const { data } = await axios.post("/api/studio/summary/analytics", {
					startDate: range.start,
					endDate: range.end,
					trendGranularity: granularity,
				});
				if (latestRequestRef.current !== requestId) return;
				setAnalytics(data?.result?.analytics || {});
				setMerchantAnalytics(data?.result?.merchantAnalytics || {});
			} catch (error) {
				if (latestRequestRef.current !== requestId) return;
				setSnackBarOpen(true);
				setSnackBar((prev) => ({ ...prev, message: parseErrorMessage(error), severity: "error" }));
			} finally {
				if (latestRequestRef.current === requestId) setLoading(false);
			}
		},
		[setSnackBar, setSnackBarOpen]
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedDateRange(dateRange), 180);
		return () => clearTimeout(timer);
	}, [dateRange.start, dateRange.end]);

	useEffect(() => {
		fetchSummaryData(debouncedDateRange, trendGranularity);
	}, [debouncedDateRange, fetchSummaryData, trendGranularity]);

	const executiveCards = useMemo(
		() => [
			{ label: "Total Shoots", value: analytics?.shootCount?.total || 0, color: grey[900] },
			{ label: "Shoots Published", value: analytics?.shootCount?.published || 0, color: green[700] },
			{ label: "Total Costings", value: analytics?.costingCount?.total || 0, color: grey[900] },
			{ label: "Costings Published", value: analytics?.costingCount?.published || 0, color: green[700] },
			{ label: "Sample Handover", value: merchantAnalytics?.handoverDone || 0, color: grey[900] },
			{ label: "Studio Inward", value: merchantAnalytics?.inwardDone || 0, color: grey[900] },
		],
		[analytics, merchantAnalytics]
	);

	const buildComputedTrendRows = useCallback((sourceRows, granularity) => {
		const orderedOldestFirst = [...(sourceRows || [])].sort((a, b) => {
			const aKey = a?.dayKey || a?.date || a?.monthKey || "";
			const bKey = b?.dayKey || b?.date || b?.monthKey || "";
			return String(aKey).localeCompare(String(bKey));
		});
		const computedRows = orderedOldestFirst.map((row, index) => {
			const prevRow = index > 0 ? orderedOldestFirst[index - 1] : null;
			const avgCostPerCosting = row?.costingCreated ? Math.round((row?.totalCost || 0) / row?.costingCreated) : 0;
			const label = granularity === "day" ? row?.dayLabel || row?.dateLabel || row?.date || row?.dayKey : row?.monthLabel;
			const rowKey = row?.dayKey || row?.date || row?.monthKey || `${label}-${index}`;
			return {
				...row,
				rowKey,
				label,
				avgCostPerCosting,
				shootCreatedTrend: buildTrendMeta(row?.shootCreated, prevRow?.shootCreated),
				costingCreatedTrend: buildTrendMeta(row?.costingCreated, prevRow?.costingCreated),
				avgCostPerCostingTrend: buildTrendMeta(avgCostPerCosting, prevRow?.costingCreated ? Math.round((prevRow?.totalCost || 0) / prevRow?.costingCreated) : 0),
				totalCostTrend: buildTrendMeta(row?.totalCost, prevRow?.totalCost),
			};
		});
		return computedRows.reverse();
	}, []);

	const trendRows = useMemo(() => {
		const sourceRows = analytics?.trendRows || (trendGranularity === "day" ? analytics?.dailyRows || [] : analytics?.monthlyRows || []);
		return buildComputedTrendRows(sourceRows, trendGranularity);
	}, [analytics?.dailyRows, analytics?.monthlyRows, analytics?.trendRows, buildComputedTrendRows, trendGranularity]);

	const costBreakdownRows = useMemo(() => {
		const previousMap = (analytics?.previousCostingBreakdown || []).reduce((acc, item) => {
			if (item?.label) acc[item.label] = item;
			return acc;
		}, {});
		return (analytics?.costingBreakdown || []).map((item) => ({
			...item,
			trend: getCostBreakdownTrend(item, previousMap),
		}));
	}, [analytics?.costingBreakdown, analytics?.previousCostingBreakdown]);

	const costingDetailsRows = useMemo(() => {
		const previous = analytics?.previousPeriod || analytics?.previousMetrics || analytics?.costingDetailsPrevious || {};
		const previousTotalCost = previous?.costingValue ?? analytics?.costingValuePrevious ?? analytics?.previousCostingValue;
		const previousAvgCostPerShoot = previous?.avgCostPerShoot ?? analytics?.avgCostPerShootPrevious ?? analytics?.previousAvgCostPerShoot;
		const previousTotalProducts = previous?.totalProductCount ?? analytics?.totalProductCountPrevious ?? analytics?.previousTotalProductCount;
		const previousAvgProductsPerShoot = previous?.avgProductPerShoot ?? analytics?.avgProductPerShootPrevious ?? analytics?.previousAvgProductPerShoot;

		return [
			{
				label: "Total Cost",
				value: analytics?.costingValue || 0,
				valueVariant: "h6",
				valueColor: green[700],
				trend: buildTrendMeta(analytics?.costingValue, previousTotalCost),
			},
			{
				label: "Avg Cost/Shoot",
				value: analytics?.avgCostPerShoot || 0,
				valueVariant: "body1",
				trend: buildTrendMeta(analytics?.avgCostPerShoot, previousAvgCostPerShoot),
			},
			{
				label: "Total Products",
				value: analytics?.totalProductCount || 0,
				valueVariant: "body1",
				trend: buildTrendMeta(analytics?.totalProductCount, previousTotalProducts),
			},
			{
				label: "Avg Products/Shoot",
				value: analytics?.avgProductPerShoot || 0,
				valueVariant: "body1",
				trend: buildTrendMeta(analytics?.avgProductPerShoot, previousAvgProductsPerShoot),
			},
		];
	}, [analytics]);

	const renderTrendText = (trend) => {
		if (!trend) return null;
		const isGrowth = trend.delta > 0;
		const isDegrowth = trend.delta < 0;
		const color = isGrowth ? green[700] : isDegrowth ? red[700] : grey[600];
		const TrendIcon = isGrowth ? ArrowDropUpRoundedIcon : isDegrowth ? ArrowDropDownRoundedIcon : RemoveRoundedIcon;
		return (
			<Box display="inline-flex" alignItems="center" color={color}>
				<TrendIcon sx={{ fontSize: 20 }} />
				<Typography variant="caption" color="inherit">
					{`${Math.abs(trend.percentage)}%`}
				</Typography>
			</Box>
		);
	};

	return (
		<Stack width="100%" spacing={2.5} pt={2} pb={4} px={2} sx={{ background: grey[50] }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
				<Box display="flex" alignItems="center" gap={1}>
					<Typography variant="h6" fontWeight={600} color={grey[900]}>
						Studio Analytics
					</Typography>
					{loading && <CircularProgress size={18} />}
				</Box>
				<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
					<Box display="flex" gap={0.5} sx={{ background: grey[100], borderRadius: 1.5, p: 0.5 }}>
						{[7, 14, 30, 90].map((days) => (
							<Button
								key={days}
								size="small"
								onClick={() => setPresetRange(days)}
								variant={selectedPreset === days ? "contained" : "text"}
								sx={{
									minWidth: 50,
									px: 1.5,
									py: 0.5,
									fontWeight: selectedPreset === days ? 600 : 500,
									background: selectedPreset === days ? grey[900] : "transparent",
									color: selectedPreset === days ? "white" : grey[700],
									"&:hover": { background: selectedPreset === days ? grey[800] : grey[200] },
								}}>
								{days}d
							</Button>
						))}
					</Box>
					<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
					<TextField
						type="date"
						size="small"
						label="Start"
						InputLabelProps={{ shrink: true }}
						value={draftRange.start}
						onChange={(e) => setDraftRange((prev) => ({ ...prev, start: e.target.value }))}
						sx={{ width: 140 }}
					/>
					<TextField
						type="date"
						size="small"
						label="End"
						InputLabelProps={{ shrink: true }}
						value={draftRange.end}
						onChange={(e) => setDraftRange((prev) => ({ ...prev, end: e.target.value }))}
						sx={{ width: 140 }}
					/>
					<Button variant="contained" size="small" onClick={handleApplyDateRange} sx={{ background: grey[900], "&:hover": { background: grey[800] } }}>
						Apply
					</Button>
				</Box>
			</Box>

			<Box display="flex" gap={2} flexWrap="wrap">
				{executiveCards.map((card) => (
					<Box key={card.label} sx={{ ...sectionCardSx, minWidth: 150, flex: 1, p: 1.5, borderLeft: `3px solid ${card.color}` }}>
						<Typography variant="caption" color={grey[600]} fontWeight={500}>
							{card.label}
						</Typography>
						<Typography variant="h5" fontWeight={600} color={card.color} mt={0.5}>
							{card.value}
						</Typography>
					</Box>
				))}
			</Box>

			<Box display="flex" gap={2} flexWrap="wrap">
				<Box sx={{ ...sectionCardSx, flex: 1, p: 1.5 }}>
					<Typography variant="caption" color={grey[600]} fontWeight={500}>
						PENDING SHOOTS
					</Typography>
					<Typography variant="h6" fontWeight={600} color={orange[700]} mt={0.5}>
						{analytics?.shootCount?.pending || 0}
					</Typography>
				</Box>
				<Box sx={{ ...sectionCardSx, flex: 1, p: 1.5 }}>
					<Typography variant="caption" color={grey[600]} fontWeight={500}>
						PENDING COSTINGS
					</Typography>
					<Typography variant="h6" fontWeight={600} color={orange[700]} mt={0.5}>
						{analytics?.costingCount?.pending || 0}
					</Typography>
				</Box>
				<Box sx={{ ...sectionCardSx, flex: 1, p: 1.5 }}>
					<Typography variant="caption" color={grey[600]} fontWeight={500}>
						OVERDUE HANDOVER
					</Typography>
					<Typography variant="h6" fontWeight={600} color={red[700]} mt={0.5}>
						{merchantAnalytics?.overdueHandover || 0}
					</Typography>
				</Box>
				<Box sx={{ ...sectionCardSx, flex: 1, p: 1.5 }}>
					<Typography variant="caption" color={grey[600]} fontWeight={500}>
						OVERDUE INWARD
					</Typography>
					<Typography variant="h6" fontWeight={600} color={red[700]} mt={0.5}>
						{merchantAnalytics?.overdueInward || 0}
					</Typography>
				</Box>
			</Box>

			<Box sx={sectionCardSx}>
				<Typography variant="body1" fontWeight={600} mb={2}>
					Shoot Costing Details
				</Typography>
				<Box display="flex" gap={2} mb={2} p={1.5} sx={{ background: alpha(green[50], 0.3), borderRadius: 1.5 }}>
					{costingDetailsRows.map((item) => (
						<Box key={item.label} flex={1}>
							<Typography variant="caption" color={grey[600]}>
								{item.label}
							</Typography>
							<Typography variant={item.valueVariant} fontWeight={600} color={item.valueColor}>
								{Number(item.value || 0).toLocaleString()}
							</Typography>
							{renderTrendText(item.trend)}
						</Box>
					))}
				</Box>
				<Typography variant="body2" fontWeight={600} mb={1.5}>
					Cost Breakdown by Category
				</Typography>
				{(analytics?.costingValue || 0) > 0 && costBreakdownRows.length > 0 ? (
					<Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={1.5}>
						{costBreakdownRows.map((item) => (
							<Box key={item.label} sx={{ background: grey[50], borderRadius: 1, p: 1.5 }}>
								<Box display="flex" justifyContent="space-between" mb={0.5}>
									<Typography variant="body2" fontWeight={500}>
										{item.label}
									</Typography>
									<Typography variant="body2" fontWeight={700} color={green[700]}>
										{item.percentage}%
									</Typography>
								</Box>
								<Typography variant="h6" fontWeight={700}>
									{(item.value || 0).toLocaleString()}
								</Typography>
								{renderTrendText(item.trend)}
							</Box>
						))}
					</Box>
				) : (
					<Box p={2} sx={{ background: grey[50], borderRadius: 1, textAlign: "center" }}>
						<Typography variant="body2" color={grey[600]}>
							No costing data available
						</Typography>
					</Box>
				)}
			</Box>

			<Box sx={sectionCardSx}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
					<Typography variant="h6" fontWeight={600}>
						{trendGranularity === "day" ? "Daily Performance Trend" : "Monthly Performance Trend"}
					</Typography>
					<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
						<Box display="flex" gap={0.5} sx={{ background: grey[100], borderRadius: 1.5, p: 0.5 }}>
							<Button
								size="small"
								variant={trendGranularity === "month" ? "contained" : "text"}
								onClick={() => setTrendGranularity("month")}
								sx={{
									minWidth: 70,
									px: 1.5,
									py: 0.5,
									fontWeight: trendGranularity === "month" ? 600 : 500,
									background: trendGranularity === "month" ? grey[900] : "transparent",
									color: trendGranularity === "month" ? "white" : grey[700],
									"&:hover": { background: trendGranularity === "month" ? grey[800] : grey[200] },
								}}>
								Month
							</Button>
							<Button
								size="small"
								variant={trendGranularity === "day" ? "contained" : "text"}
								onClick={() => setTrendGranularity("day")}
								sx={{
									minWidth: 70,
									px: 1.5,
									py: 0.5,
									fontWeight: trendGranularity === "day" ? 600 : 500,
									background: trendGranularity === "day" ? grey[900] : "transparent",
									color: trendGranularity === "day" ? "white" : grey[700],
									"&:hover": { background: trendGranularity === "day" ? grey[800] : grey[200] },
								}}>
								Day
							</Button>
						</Box>
					</Box>
				</Box>
				<Box>
					<Box display="flex" px={2} py={1.5} sx={{ background: grey[100], borderRadius: 2 }}>
						<Typography width="22%" variant="body2" fontWeight={600} color={grey[700]}>
							{trendGranularity === "day" ? "Date" : "Month"}
						</Typography>
						<Typography width="18%" variant="body2" fontWeight={600} color={grey[700]}>
							Shoots
						</Typography>
						<Typography width="18%" variant="body2" fontWeight={600} color={grey[700]}>
							Costings
						</Typography>
						<Typography width="20%" variant="body2" fontWeight={600} color={grey[700]} textAlign="right">
							Avg Costing
						</Typography>
						<Typography width="26%" variant="body2" fontWeight={600} color={grey[700]} textAlign="right">
							Total Cost
						</Typography>
					</Box>

					{trendRows.map((row, index) => (
						<Box key={row.rowKey}>
							<Box display="flex" px={2} py={1.5} sx={{ "&:hover": { background: grey[50] } }}>
								<Typography width="22%" variant="body2" fontWeight={500}>
									{row.label}
								</Typography>
								<Box width="18%" display="flex" flexDirection="column" alignItems="flex-start">
									<Typography variant="body2">{row.shootCreated}</Typography>
									{renderTrendText(row.shootCreatedTrend)}
								</Box>
								<Box width="18%" display="flex" flexDirection="column" alignItems="flex-start">
									<Typography variant="body2">{row.costingCreated}</Typography>
									{renderTrendText(row.costingCreatedTrend)}
								</Box>
								<Box width="20%" display="flex" flexDirection="column" alignItems="flex-end">
									<Typography variant="body2" textAlign="right" fontWeight={500}>
										{row.avgCostPerCosting.toLocaleString()}
									</Typography>
									{renderTrendText(row.avgCostPerCostingTrend)}
								</Box>
								<Box width="26%" display="flex" flexDirection="column" alignItems="flex-end">
									<Typography variant="body2" textAlign="right" fontWeight={500}>
										{(row.totalCost || 0).toLocaleString()}
									</Typography>
									{renderTrendText(row.totalCostTrend)}
								</Box>
							</Box>
							{index < trendRows.length - 1 && <Divider />}
						</Box>
					))}

					{!loading && trendRows.length === 0 && (
						<Typography variant="body2" color={grey[600]} px={2} py={2}>
							No {trendGranularity} trend data available for selected range
						</Typography>
					)}
				</Box>
			</Box>
		</Stack>
	);
};

export default ShootSummary;
